import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { fetchProjects } from '../data/projectSlice';
import { useBackendTranslation } from '../utils/useBackendTranslation';
import BackendText, { BackendHeading } from './BackendText';
import CategoryCard from './CategoryCard';
import ProjectCard from './ProjectCard';
import StatsCard from './StatsCard';

const iconFor = (key) => {
  const map = {
    legal_aid: '⚖️',
    emergency: '⛑️',
    education: '📚',
    women_child: '👩‍👧',
    health: '🏥',
    livelihoods: '🧵',
    agri_env: '🌱',
    culture_sports: '🎭'
  };
  return map[key] || '✅';
};

// safe array reader for i18n
const useArr = (t, key) => {
  const v = t(key, { returnObjects: true, defaultValue: [] });
  if (Array.isArray(v)) return v;
  if (v && typeof v === 'object') return Object.values(v);
  return [];
};

const Projects = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // from Redux (data from backend)
  const { title, project, status } = useSelector((state) => state.project);

  // Use our custom hook to translate backend data
  const { translatedData: translatedProjects, isLoading: isTranslating } = useBackendTranslation(project);
  const { translatedData: translatedTitle } = useBackendTranslation(title);

  // Fetch projects on component mount
  useEffect(() => {
    if (status === 'idle') {
      dispatch(fetchProjects());
    }
  }, [dispatch, status]);

  // from i18n (new, detailed categories)
  const categories = useArr(t, 'projects_page.categories');

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-black text-white py-20 lg:py-32 relative overflow-hidden">
        {/* Geometric Background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-64 h-64 border-2 border-white transform rotate-45"></div>
          <div className="absolute bottom-20 right-10 w-48 h-48 border border-white transform -rotate-12"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 border border-white rounded-full"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-block border-2 border-white px-6 py-3 mb-8">
              <span className="text-sm font-bold uppercase tracking-widest">{t('projects_page.our_impact', 'Our Impact')}</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black mb-8 leading-tight uppercase tracking-wide">
              {t('projects_page.title')}
            </h1>
            
            <div className="flex items-center justify-center mb-8">
              <div className="w-24 h-1 bg-white"></div>
              <div className="w-4 h-4 bg-white mx-6 transform rotate-45"></div>
              <div className="w-24 h-1 bg-white"></div>
            </div>
            
            <p className="text-xl lg:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed font-light">
              {t('projects_page.intro')}
            </p>
          </div>
        </div>
      </section>

      {/* Featured Programs (Redux) */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-black mb-4 uppercase tracking-wider text-black">
              {/* Use translated title from backend or loading state */}
              {status === 'loading' ? t('loading') : translatedTitle || t('projects')}
            </h2>
            <div className="w-24 h-1 bg-black mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('projects_page.subtitle', 'Transforming lives through targeted interventions and sustainable solutions')}
            </p>
          </div>
          
          {/* Show loading state if loading */}
          {(status === 'loading' || isTranslating) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 lg:gap-12">
              {[1, 2, 3].map((item) => (
                <div key={item} className="animate-pulse">
                  <div className="h-64 bg-gray-200 mb-4"></div>
                  <div className="h-6 bg-gray-200 w-3/4 mb-4"></div>
                  <div className="h-4 bg-gray-200 w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 w-3/4"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 lg:gap-12">
              {Array.isArray(translatedProjects) && translatedProjects.map((item, index) => (
                <ProjectCard 
                  key={item.id} 
                  project={item} 
                  index={index} 
                  isBackendContent={true}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Programs & Focus Areas */}
      <section className="py-20 lg:py-32 bg-gray-50 relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-40">
          <div className="absolute inset-0"
          style={{
              backgroundImage: `url("/assets/labour.png")`,
              backgroundRepeat: 'repeat'
            }}></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <BackendHeading
              i18nKey="how_we_help_laborers"
              fallback="How We Help Laborers"
              className="text-3xl lg:text-4xl font-black mb-4 uppercase tracking-wider text-black"
            />
            <div className="w-24 h-1 bg-black mx-auto mb-6"></div>
            <BackendText
              i18nKey="projects_page.holistic_support"
              fallback="Comprehensive support across multiple domains for holistic development"
              as="p"
              className="text-lg text-gray-600 max-w-3xl mx-auto"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {categories.map((cat, idx) => (
              <CategoryCard 
                key={idx}
                category={cat}
                index={idx}
                icon={iconFor(cat.key)}
                isBackendContent={false} // This is i18n content, not backend content
              />
            ))}
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-20 bg-black text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <StatsCard
              value="500+"
              label="Projects Completed"
              i18nKey="projects_page.stats.projects_completed"
            />
            <StatsCard
              value="10K+"
              label="Lives Impacted"
              i18nKey="projects_page.stats.lives_impacted"
            />
            <StatsCard
              value="50+"
              label="Communities"
              i18nKey="projects_page.stats.communities"
            />
            <StatsCard
              value="8"
              label="Focus Areas"
              i18nKey="projects_page.stats.focus_areas"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-white border-t-4 border-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl lg:text-4xl font-black mb-8 uppercase tracking-wider text-black">
            {t('projects_page.cta_title', 'Ready to Make an Impact?')}
          </h3>
          <div className="w-24 h-1 bg-black mx-auto mb-12"></div>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center max-w-lg mx-auto">
            <Link
              to="/contact"
              className="group border-2 border-black px-8 py-4 font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-all duration-300 relative overflow-hidden"
            >
              <span className="relative z-10 flex items-center justify-center">
                {t('contact')}
                <svg className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </span>
            </Link>
            
            <Link
              to="/donate"
              className="group bg-black text-white px-8 py-4 font-bold uppercase tracking-wider hover:bg-gray-800 transition-all duration-300 relative overflow-hidden"
            >
              <span className="relative z-10 flex items-center justify-center">
                {t('donate_now')}
                <svg className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;